// src/core/adapters/LondonMetrics.js
//
// TfL-shaped metric derivation. Knows about naptan_id, timing_point_code,
// rule_applied, exclude_30_rule, arrival_hour, and the 30% Rule.
// Pure functions. No React, no styles, no I/O.

export const groupHourData = (hourData, preferRuleApplied) => {
  const byHour = new Map();
  hourData.forEach(item => {
    const hour = String(item.hour);
    if (!byHour.has(hour)) byHour.set(hour, []);
    byHour.get(hour).push(item);
  });

  return Array.from(byHour.values()).flatMap(items => {
    const preferred = items.filter(item =>
      preferRuleApplied
        ? item.rule_applied === true
        : item.rule_applied === false
    );
    return preferred.length > 0 ? preferred : items;
  });
};

export const buildHourLookup = (hourData, preferRuleApplied) => {
  const lookup = {};
  groupHourData(hourData, preferRuleApplied).forEach(item => {
    lookup[item.hour] = { ewt: item.ewt_hour, otp: item.otp_hour };
  });
  return lookup;
};

export const buildStopLookup = (stopData, apply30Rule) => {
  let filtered = stopData;
  if (apply30Rule) {
    filtered = stopData.filter(item => item.exclude_30_rule === false);
    if (filtered.length === 0) filtered = stopData; // fallback to all stops
  }

  const lookup = {};
  filtered.forEach(item => {
    const hour = item.arrival_hour;
    if (!lookup[hour]) lookup[hour] = {};
    const directionKey = item.direction === 1 ? 'outbound' : 'inbound';
    const key = `${item.naptan_id}-${directionKey}`;
    lookup[hour][key] = { ewt: item.ewt_stop, otp: item.otp_stop };
  });
  return lookup;
};

export const collectHours = (hourLookup, stopLookup) => {
  const hourSet = new Set();
  Object.keys(hourLookup).forEach(h => hourSet.add(parseInt(h)));
  Object.keys(stopLookup).forEach(h => hourSet.add(parseInt(h)));
  return Array.from(hourSet).sort((a, b) => a - b);
};

export const buildTimingPoints = (stops, directionFilter) => {
  const outbound = (stops.outbound || [])
    .filter(s => s.type === 1 && s.timing_point_code && String(s.timing_point_code).trim() !== '')
    .map(s => ({ ...s, direction: 'outbound' }));
  const inbound = (stops.inbound || [])
    .filter(s => s.type === 1 && s.timing_point_code && String(s.timing_point_code).trim() !== '')
    .map(s => ({ ...s, direction: 'inbound' }));

  let all = [...outbound, ...inbound];
  if (directionFilter === 'outbound') all = all.filter(s => s.direction === 'outbound');
  else if (directionFilter === 'inbound') all = all.filter(s => s.direction === 'inbound');

  const seen = new Set();
  const unique = all.filter(s => {
    const key = `${s.naptan_id}-${s.direction}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const bySeq = (a, b) => (a.sequence_no || 0) - (b.sequence_no || 0);
  const outboundSorted = unique.filter(s => s.direction === 'outbound').sort(bySeq);
  const inboundSorted = unique.filter(s => s.direction === 'inbound').sort(bySeq);
  return [...outboundSorted, ...inboundSorted];
};

// Only used here. Not exported.
const weightedAverage = (pairs) => {
  const valid = pairs.filter(([v, w]) => v != null && !isNaN(v) && w != null && w > 0);
  if (!valid.length) return null;
  const sumWeighted = valid.reduce((s, [v, w]) => s + v * w, 0);
  const sumWeights = valid.reduce((s, [v, w]) => s + w, 0);
  return sumWeighted / sumWeights;
};

export const computeDailyAggregates = (hourData, pjfData, preferRuleApplied) => {
  if (!pjfData || Object.keys(pjfData).length === 0) return null;
  const data = groupHourData(hourData, preferRuleApplied);
  if (data.length === 0) return null;

  const ewtPairs = data.map(h => [h.ewt_hour, pjfData[h.hour] || 0]);
  const otpPairs = data.map(h => [h.otp_hour, pjfData[h.hour] || 0]);

  return {
    ewt: weightedAverage(ewtPairs),
    otp: weightedAverage(otpPairs),
  };
};